"use client";

import { useEffect, useRef } from "react";
import { stripEndToken } from "@lib/agent/conversation/endToken";
import type { OverlayMessage } from "../types";

type ConversationTranscriptProps = {
  messages: OverlayMessage[];
  messagesEndRef?: React.RefObject<HTMLDivElement | null>;
};

function displayPersonaText(text: string): string {
  return stripEndToken(text).display;
}

export function ConversationTranscript({ messages, messagesEndRef }: ConversationTranscriptProps) {
  const localEndRef = useRef<HTMLDivElement>(null);
  const endRef = messagesEndRef ?? localEndRef;

  const conversationMessages = messages.filter((m) => m.role === "user" || m.role === "persona");

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [conversationMessages, endRef]);

  if (conversationMessages.length === 0) {
    return null;
  }

  return (
    <ul className="flex flex-col gap-2">
      {conversationMessages.map((msg) => {
        const isLearner = msg.role === "user";
        const displayText = msg.role === "persona" ? displayPersonaText(msg.text) : msg.text;

        return (
          <li key={msg.id} className={isLearner ? "flex justify-end" : "flex justify-start"}>
            <div
              className="max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-relaxed"
              style={
                isLearner
                  ? {
                      background:
                        "linear-gradient(135deg, var(--speaking-light) 0%, var(--speaking) 100%)",
                      color: "white",
                      borderBottomRightRadius: "4px",
                    }
                  : {
                      background: "var(--surface-sunk)",
                      color: "var(--ink-body)",
                      borderBottomLeftRadius: "4px",
                      boxShadow: "var(--inset-field)",
                    }
              }
            >
              {displayText || (msg.isStreaming ? " " : "")}
              {msg.isStreaming && (
                <span
                  className="ml-0.5 inline-block h-3.5 w-0.5 animate-pulse align-middle"
                  style={{ background: "var(--ink-faint)" }}
                  aria-hidden="true"
                />
              )}
            </div>
          </li>
        );
      })}
      <div ref={endRef} />
    </ul>
  );
}
